import { useState, useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { DonationForm } from "@/components/DonationForm";
import { supabase } from "@/integrations/supabase/client";
import { useSiteContent } from "@/hooks/useSiteContent";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { formatDateForDisplay } from "@/lib/utils";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

interface MissionTrip {
  id: string;
  title: string;
  description: string | null;
  trip_date: string | null;
  photos: any[];
}

const CubaMission = () => {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [allImages, setAllImages] = useState<any[]>([]);
  const [trips, setTrips] = useState<MissionTrip[]>([]);
  const [loading, setLoading] = useState(true);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [currentPages, setCurrentPages] = useState<Record<string, number>>({});
  const { t } = useLanguage();
  const { content } = useSiteContent('cuba_mission');

  const minSwipeDistance = 50;
  const PHOTOS_PER_PAGE = 4;

  // Helper functions for YouTube video handling
  const getYouTubeVideoId = (url: string) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const getYouTubeEmbedUrl = (url: string) => {
    const videoId = getYouTubeVideoId(url);
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  };

  useEffect(() => {
    const fetchTripsAndPhotos = async () => {
      // Fetch trips
      const { data: tripsData, error: tripsError } = await supabase
        .from('mission_trips')
        .select('*')
        .eq('mission', 'cuba')
        .order('display_order', { ascending: true });

      if (tripsError) {
        console.error('Error fetching trips:', tripsError);
        setLoading(false);
        return;
      }

      // Fetch photos for each trip
      const tripsWithPhotos = await Promise.all(
        (tripsData || []).map(async (trip) => {
          const { data: photosData } = await supabase
            .from('mission_photos')
            .select('*')
            .eq('trip_id', trip.id)
            .order('display_order', { ascending: true });

          return {
            ...trip,
            photos: photosData || []
          };
        })
      );

      setTrips(tripsWithPhotos);
      
      // Flatten all images for lightbox navigation
      const allImages = tripsWithPhotos.flatMap(trip => trip.photos);
      setAllImages(allImages);
      
      setLoading(false);
    };

    fetchTripsAndPhotos();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedImage === null) return;
      
      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'Escape') {
        setSelectedImage(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, allImages]);

  const goToPrevious = () => {
    if (selectedImage !== null && selectedImage > 0) {
      setSelectedImage(selectedImage - 1);
    }
  };

  const goToNext = () => {
    if (selectedImage !== null && selectedImage < allImages.length - 1) {
      setSelectedImage(selectedImage + 1);
    }
  };

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      goToNext();
    }
    if (isRightSwipe) {
      goToPrevious();
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-24 pb-20">
        <div className="container mx-auto px-4">
          {/* Header Section */}
          <div className="text-center mb-16 animate-fade-in">
            <h1 className="text-5xl md:text-6xl font-bold text-foreground mb-6">
              {content.title || "Cuba Mission"}
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto whitespace-pre-line">
              {content.subtitle || "Bringing hope to the Cuban people"}
            </p>
          </div>

          {/* Mission Info Section */}
          <div className="mb-16 bg-card rounded-lg p-8 md:p-12 shadow-lg">
            <h2 className="text-3xl font-bold text-foreground mb-6">{content.ourwork || "Our Work in Cuba"}</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-foreground mb-4">{content.medicalcare || "Medical Support"}</h3>
                <p className="text-muted-foreground mb-6 whitespace-pre-line">
                  {content.medicalcare_desc || "Delivering medical supplies and healthcare training."}
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-foreground mb-4">{content.communityoutreach || "Humanitarian Aid"}</h3>
                <p className="text-muted-foreground mb-6 whitespace-pre-line">
                  {content.communityoutreach_desc || "Providing food and essential supplies."}
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-foreground mb-4">{content.spiritualgrowth || "Church Support"}</h3>
                <p className="text-muted-foreground mb-6 whitespace-pre-line">
                  {content.spiritualgrowth_desc || "Encouraging Cuban pastors and believers."}
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-foreground mb-4">{content.infrastructure || "Resource Development"}</h3>
                <p className="text-muted-foreground mb-6 whitespace-pre-line">
                  {content.infrastructure_desc || "Supporting church buildings and resources."}
                </p>
              </div>
            </div>
          </div>

          {/* Photo Gallery Section - Grouped by Trips */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold text-center text-foreground mb-12">{content.gallery || "Mission Trips Gallery"}</h2>
            
            {loading ? (
              <p className="text-center text-muted-foreground">Loading gallery...</p>
            ) : trips.length === 0 ? (
              <p className="text-center text-muted-foreground">No trips available yet.</p>
            ) : (
              <div className="space-y-16">
                {trips.map((trip, tripIndex) => {
                  if (trip.photos.length === 0) return null;
                  
                  const currentPage = currentPages[trip.id] || 1;
                  const totalPages = Math.ceil(trip.photos.length / PHOTOS_PER_PAGE);
                  const startIndex = (currentPage - 1) * PHOTOS_PER_PAGE;
                  const endIndex = startIndex + PHOTOS_PER_PAGE;
                  const visiblePhotos = trip.photos.slice(startIndex, endIndex);

                  return (
                    <div key={trip.id} className="space-y-6">
                      <div className="text-center">
                        <h3 className="text-3xl font-bold text-foreground mb-2">{trip.title}</h3>
                        {trip.description && (
                          <p className="text-muted-foreground max-w-2xl mx-auto whitespace-pre-line">{trip.description}</p>
                        )}
                        {trip.trip_date && (
                          <p className="text-sm text-muted-foreground mt-2">
                            {formatDateForDisplay(trip.trip_date)}
                          </p>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {visiblePhotos.map((item, photoIndex) => {
                          const globalIndex = allImages.findIndex(img => img.id === item.id);
                          const isVideo = item.media_type === 'video';
                          const isYouTube = item.video_url && (item.video_url.includes('youtube.com') || item.video_url.includes('youtu.be'));

                          return (
                            <Card
                              key={item.id}
                              className="overflow-hidden cursor-pointer hover:shadow-xl transition-shadow"
                              onClick={() => !isYouTube && setSelectedImage(globalIndex)}
                            >
                              <div className="relative pb-[75%]">
                                {isVideo ? (
                                  isYouTube ? (
                                    <iframe
                                      className="absolute inset-0 w-full h-full"
                                      src={getYouTubeEmbedUrl(item.video_url) || ''}
                                      title={item.title}
                                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                      allowFullScreen
                                    />
                                  ) : (
                                    <video
                                      className="absolute inset-0 w-full h-full object-cover"
                                      src={item.video_url}
                                      controls
                                    />
                                  )
                                ) : (
                                  <img
                                    src={item.image_url}
                                    alt={item.title}
                                    className="absolute inset-0 w-full h-full object-cover"
                                  />
                                )}
                              </div>
                              <div className="p-4">
                                 <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                                 {item.description && (
                                   <p className="text-sm text-muted-foreground whitespace-pre-line">{item.description}</p>
                                 )}
                              </div>
                            </Card>
                          );
                        })}
                      </div>

                      {/* Pagination - Desktop Only */}
                      {totalPages > 1 && (
                        <div className="hidden md:flex justify-center mt-8">
                          <Pagination>
                            <PaginationContent>
                              <PaginationItem>
                                <PaginationPrevious 
                                  onClick={() => {
                                    if (currentPage > 1) {
                                      setCurrentPages(prev => ({ ...prev, [trip.id]: currentPage - 1 }));
                                    }
                                  }}
                                  className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                                />
                              </PaginationItem>
                              
                              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                <PaginationItem key={page}>
                                  <PaginationLink
                                    onClick={() => setCurrentPages(prev => ({ ...prev, [trip.id]: page }))}
                                    isActive={currentPage === page}
                                    className="cursor-pointer"
                                  >
                                    {page}
                                  </PaginationLink>
                                </PaginationItem>
                              ))}
                              
                              <PaginationItem>
                                <PaginationNext 
                                  onClick={() => {
                                    if (currentPage < totalPages) {
                                      setCurrentPages(prev => ({ ...prev, [trip.id]: currentPage + 1 }));
                                    }
                                  }}
                                  className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                                />
                              </PaginationItem>
                            </PaginationContent>
                          </Pagination>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Lightbox */}
          {selectedImage !== null && allImages[selectedImage] && (
            <div 
              className="fixed inset-0 bg-black/90 z-50 flex flex-col items-center justify-center p-2 md:p-4"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-4 right-4 text-white hover:bg-white/20"
                onClick={() => setSelectedImage(null)}
              >
                <X className="h-6 w-6" />
              </Button>

              <Button
                variant="ghost"
                size="icon"
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 disabled:opacity-30"
                onClick={goToPrevious}
                disabled={selectedImage === 0}
              >
                <ChevronLeft className="h-8 w-8" />
              </Button>

              <Button
                variant="ghost"
                size="icon"
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 disabled:opacity-30"
                onClick={goToNext}
                disabled={selectedImage === allImages.length - 1}
              >
                <ChevronRight className="h-8 w-8" />
              </Button>

              <div className="w-full h-full max-w-5xl flex flex-col">
                <div className="flex-1 flex items-center justify-center min-h-0">
                  {allImages[selectedImage].media_type === 'video' ? (
                    allImages[selectedImage].video_url && 
                    (allImages[selectedImage].video_url.includes('youtube.com') || 
                     allImages[selectedImage].video_url.includes('youtu.be')) ? (
                      <iframe
                        className="w-full h-full max-h-[85vh]"
                        src={getYouTubeEmbedUrl(allImages[selectedImage].video_url) || ''}
                        title={allImages[selectedImage].title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <video
                        className="w-full h-full max-h-[85vh] object-contain"
                        src={allImages[selectedImage].video_url}
                        controls
                        autoPlay
                      />
                    )
                  ) : (
                    <img
                      src={allImages[selectedImage].image_url}
                      alt={allImages[selectedImage].title}
                      className="w-full h-full object-contain max-h-[85vh]"
                    />
                  )}
                </div>
                <div className="text-white text-center mt-2 px-2 pb-2">
                  <h3 className="text-lg md:text-xl font-semibold mb-1">{allImages[selectedImage].title}</h3>
                  {allImages[selectedImage].description && (
                    <p className="text-sm md:text-base text-gray-300">{allImages[selectedImage].description}</p>
                  )}
                </div>
              </div>
            </div>
          )}

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CubaMission;